import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import Select from '../components/Select';
import ModalPlantarNaSafra from '../components/ModalVincularLavoura';
import Modal from '../components/ui/Modal';
import Field, { FormRow } from '../components/ui/Field';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { SkeletonLista } from '../components/Skeleton';
import { toast } from '../components/Toast';
import { unidadeArea } from '../utils/unidades';

const CULTURAS = [
  { value: 'SOJA', label: 'Soja' }, { value: 'MILHO', label: 'Milho' },
  { value: 'TRIGO', label: 'Trigo' }, { value: 'UVA', label: 'Uva' },
  { value: 'BATATA', label: 'Batata' }, { value: 'FUMO', label: 'Fumo' },
  { value: 'CEBOLA', label: 'Cebola' }, { value: 'MORANGA', label: 'Moranga' },
  { value: 'MORANGO', label: 'Morango' }, { value: 'TOMATE', label: 'Tomate' },
  { value: 'FLORES', label: 'Flores' }, { value: 'OUTROS', label: 'Outros' },
];

export default function Safras() {
  const navigate = useNavigate();
  const [safras, setSafras] = useState([]);
  const [lavouras, setLavouras] = useState([]);
  const [safraId, setSafraId] = useState('');
  const [safraLavouras, setSafraLavouras] = useState([]);
  const [carregandoLavouras, setCarregandoLavouras] = useState(false);
  const [mostrarFechadas, setMostrarFechadas] = useState(false);

  // Formulário de nova safra
  const [novaSafraAberta, setNovaSafraAberta] = useState(false);
  const [descricaoNovaSafra, setDescricaoNovaSafra] = useState('');
  const [culturaNovaSafra, setCulturaNovaSafra] = useState('SOJA');
  const [criando, setCriando] = useState(false);

  // Um único estado para qual diálogo está aberto, no lugar de cinco booleanos
  // independentes que podiam ficar ligados ao mesmo tempo.
  const [dialogo, setDialogo] = useState(null); // { tipo, dados }
  const [salvando, setSalvando] = useState(false);
  const [erroModal, setErroModal] = useState('');

  const fecharDialogo = () => { setDialogo(null); setErroModal(''); };

  const safraSelecionada = safras.find(s => String(s.id) === String(safraId));
  const fechada = safraSelecionada?.status === 'FECHADA';

  // Safras fechadas ficam escondidas por padrão para o seletor não crescer sem fim.
  const safrasVisiveis = safras.filter(s => mostrarFechadas || s.status !== 'FECHADA');
  const totalFechadas = safras.filter(s => s.status === 'FECHADA').length;

  const carregar = useCallback(async () => {
    try {
      const list = await api.getSafras();
      setSafras(list);
      // Na primeira carga, seleciona a safra aberta mais recente.
      setSafraId(atual => {
        if (atual || list.length === 0) return atual;
        const abertas = list.filter(s => s.status !== 'FECHADA');
        const alvo = abertas.length > 0 ? abertas[abertas.length - 1] : list[list.length - 1];
        return String(alvo.id);
      });
    } catch (e) {
      toast(e.message || 'Não foi possível carregar as safras.', 'error');
    }
    api.getLavouras().then(setLavouras).catch(() => setLavouras([]));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarTalhoes = useCallback((id) => api.getLavourasAtivasDaSafra(id).then(setSafraLavouras), []);

  useEffect(() => {
    if (!safraId) { setSafraLavouras([]); return; }
    setCarregandoLavouras(true);
    carregarTalhoes(safraId)
      .catch(() => setSafraLavouras([]))
      .finally(() => setCarregandoLavouras(false));
  }, [safraId, carregarTalhoes]);

  /** Envolve as ações de modal: trava o botão, mostra erro e fecha ao dar certo. */
  const executar = async (acao, sucesso, { recarregarSafras, recarregarTalhoes } = {}) => {
    setSalvando(true);
    setErroModal('');
    try {
      const r = await acao();
      fecharDialogo();
      toast(typeof sucesso === 'function' ? sucesso(r) : sucesso, 'success');
      if (recarregarSafras) await carregar();
      if (recarregarTalhoes && safraId) carregarTalhoes(safraId);
      return r;
    } catch (err) {
      setErroModal(err.message || 'Não foi possível concluir a operação.');
      return null;
    } finally {
      setSalvando(false);
    }
  };

  const criarSafra = async (e) => {
    e.preventDefault();
    if (!descricaoNovaSafra.trim()) return;
    setCriando(true);
    try {
      const nova = await api.criarSafra({ descricao: descricaoNovaSafra.trim(), cultura: culturaNovaSafra });
      setDescricaoNovaSafra('');
      setCulturaNovaSafra('SOJA');
      setNovaSafraAberta(false);
      toast('Safra criada!', 'success');
      await carregar();
      setSafraId(String(nova.id));
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setCriando(false);
    }
  };

  const plantarNaSafra = (form) => executar(
    () => api.vincularLavoura(form.safraId, {
      lavouraId: parseInt(form.lavouraId),
      tipoGrao: form.tipoGrao,
      variedade: form.variedade || null,
      areaHectares: form.areaHectares || null,
    }),
    'Plantio registrado!',
    { recarregarTalhoes: true }
  );

  const reabrir = async () => {
    try {
      await api.reabrirSafra(safraId);
      toast('Safra reaberta.', 'success');
      carregar();
    } catch (err) { toast(err.message, 'error'); }
  };

  const d = dialogo?.dados;
  const erroBox = erroModal ? <div className="aviso aviso--erro" role="alert">{erroModal}</div> : null;

  return (
    <div>
      {/* ── Diálogos ── */}
      {dialogo?.tipo === 'plantar' && (
        <ModalPlantarNaSafra
          safraId={safraId}
          safraNome={safraSelecionada?.descricao}
          lavouras={lavouras}
          safraLavouras={safraLavouras}
          onConfirmar={plantarNaSafra}
          onClose={fecharDialogo}
          erro={erroModal}
        />
      )}

      {dialogo?.tipo === 'editarPlantio' && (
        <Modal
          titulo={`Editar plantio — ${d.lavoura?.descricao || ''}`}
          onClose={fecharDialogo}
          largura={520}
          rodape={<>
            <button className="btn-secondary" onClick={fecharDialogo} disabled={salvando}>Cancelar</button>
            <button
              className="btn-primary"
              disabled={salvando}
              onClick={() => executar(
                () => api.editarSafraLavoura(d.id, {
                  tipoGrao: d.tipoGrao,
                  variedade: d.variedade || null,
                  areaHectares: d.areaHectares ? parseFloat(d.areaHectares) : null,
                }),
                'Plantio atualizado!',
                { recarregarTalhoes: true }
              )}
            >{salvando ? 'Salvando...' : 'Salvar'}</button>
          </>}
        >
          {erroBox}
          <FormRow>
            <Field label="O que foi plantado" largura="cheia">
              <Select
                value={d.tipoGrao}
                onChange={v => setDialogo(x => ({ ...x, dados: { ...x.dados, tipoGrao: v } }))}
                options={CULTURAS}
              />
            </Field>
            <Field label="Variedade" hint="Opcional">
              <input
                value={d.variedade || ''}
                onChange={e => setDialogo(x => ({ ...x, dados: { ...x.dados, variedade: e.target.value } }))}
              />
            </Field>
            <Field label={`Área plantada (${unidadeArea(d.lavoura?.tipoArea)})`}>
              <input
                type="number" step="0.1" min="0.1"
                value={d.areaHectares}
                onChange={e => setDialogo(x => ({ ...x, dados: { ...x.dados, areaHectares: e.target.value } }))}
              />
            </Field>
          </FormRow>
        </Modal>
      )}

      {dialogo?.tipo === 'removerPlantio' && (
        <Modal
          titulo="Remover da safra"
          onClose={fecharDialogo}
          largura={460}
          rodape={<>
            <button className="btn-secondary" onClick={fecharDialogo} disabled={salvando}>Cancelar</button>
            <button
              className="btn-danger"
              disabled={salvando}
              onClick={() => executar(
                () => api.excluirSafraLavoura(d.id),
                'Área removida da safra.',
                { recarregarTalhoes: true }
              )}
            >{salvando ? 'Removendo...' : 'Remover'}</button>
          </>}
        >
          {erroBox}
          <p className="texto-modal">
            Remover <strong>{d.lavoura?.descricao}</strong> da safra {safraSelecionada?.descricao}?
          </p>
          <div className="aviso aviso--erro">
            Todos os custos registrados nesta área serão perdidos.
          </div>
        </Modal>
      )}

      {dialogo?.tipo === 'renomear' && (
        <Modal
          titulo="Renomear safra"
          onClose={fecharDialogo}
          largura={440}
          rodape={<>
            <button className="btn-secondary" onClick={fecharDialogo} disabled={salvando}>Cancelar</button>
            <button
              className="btn-primary"
              disabled={salvando || !d.descricao?.trim()}
              onClick={() => executar(
                () => api.editarSafra(d.id, { descricao: d.descricao.trim() }),
                'Safra atualizada!',
                { recarregarSafras: true }
              )}
            >{salvando ? 'Salvando...' : 'Salvar'}</button>
          </>}
        >
          {erroBox}
          <FormRow>
            <Field label="Nome da safra" largura="cheia" obrigatorio>
              <input
                value={d.descricao}
                onChange={e => setDialogo(x => ({ ...x, dados: { ...x.dados, descricao: e.target.value } }))}
              />
            </Field>
          </FormRow>
        </Modal>
      )}

      {dialogo?.tipo === 'fechar' && (
        <Modal
          titulo={`Fechar safra ${safraSelecionada?.descricao || ''}`}
          onClose={fecharDialogo}
          largura={520}
          rodape={<>
            <button className="btn-secondary" onClick={fecharDialogo} disabled={salvando}>Cancelar</button>
            <button
              className="btn-primary"
              disabled={salvando}
              onClick={() => executar(
                () => api.fecharSafra(safraId),
                (s) => `Safra ${s.descricao} fechada.`,
                { recarregarSafras: true }
              )}
            >{salvando ? 'Fechando...' : 'Fechar safra'}</button>
          </>}
        >
          {erroBox}
          <div className="texto-modal">
            <p>Depois de fechada, a safra fica somente leitura. Deixa de ser possível:</p>
            <ul>
              <li>renomear a safra</li>
              <li>plantar, editar ou remover áreas</li>
              <li>registrar, editar ou excluir custos</li>
              <li>registrar, editar ou excluir produção</li>
            </ul>
            <p>
              <strong>Vendas continuam liberadas</strong>, para você vender o que já foi colhido.
            </p>
            <p>A safra sai do seletor, mas você pode exibi-la e reabri-la quando quiser.</p>
          </div>
        </Modal>
      )}

      {dialogo?.tipo === 'excluir' && (
        <Modal
          titulo="Excluir safra"
          onClose={fecharDialogo}
          largura={480}
          rodape={<>
            <button className="btn-secondary" onClick={fecharDialogo} disabled={salvando}>Cancelar</button>
            <button
              className="btn-danger"
              disabled={salvando}
              onClick={async () => {
                const r = await executar(
                  () => api.excluirSafra(safraId).then(() => true),
                  'Safra excluída.',
                );
                if (r) { setSafraId(''); carregar(); }
              }}
            >{salvando ? 'Excluindo...' : 'Excluir'}</button>
          </>}
        >
          {erroBox}
          <div className="texto-modal">
            <p>Excluir a safra <strong>{safraSelecionada?.descricao}</strong> em definitivo?</p>
            <p>
              Só é possível excluir safra sem áreas vinculadas, produção ou vendas. Se houver
              qualquer registro, a exclusão é recusada — nesse caso use <strong>Fechar safra</strong>.
            </p>
          </div>
        </Modal>
      )}

      <PageHeader
        titulo="Safras"
        icone="safra"
        descricao="Cada safra ou ciclo reúne as áreas plantadas, os custos e a produção do período."
        acao={{ label: 'Nova safra', icone: 'adicionar', onClick: () => setNovaSafraAberta(true) }}
      />

      {novaSafraAberta && (
        <Modal titulo="Nova safra / ciclo" onClose={() => setNovaSafraAberta(false)} largura={480}>
          <form onSubmit={criarSafra}>
            <FormRow min={150}>
              <Field label="Nome da safra" obrigatorio hint="Ex: 25/26, Verão 2026">
                <input
                  value={descricaoNovaSafra}
                  onChange={e => setDescricaoNovaSafra(e.target.value)}
                  placeholder="Ex: 25/26"
                  autoFocus
                  required
                />
              </Field>
              <Field label="Cultura">
                <Select value={culturaNovaSafra} onChange={setCulturaNovaSafra} options={CULTURAS} />
              </Field>
            </FormRow>
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setNovaSafraAberta(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={criando}>
                {criando ? 'Criando...' : 'Criar safra'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      <div className="card">
        {/* ── Seletor de safra e ações do ciclo ── */}
        <div className="barra-safra">
          {safras.length > 0 && (
            <FormRow>
              <Field label="Safra" largura="cheia">
                <Select
                  value={safraId}
                  onChange={setSafraId}
                  options={safrasVisiveis.map(s => ({
                    value: s.id,
                    label: s.status === 'FECHADA' ? `${s.descricao} (fechada)` : s.descricao,
                  }))}
                  placeholder="Selecione a safra"
                />
              </Field>
            </FormRow>
          )}

          {totalFechadas > 0 && (
            <label className="check-linha check-linha--fraca">
              <input
                type="checkbox"
                checked={mostrarFechadas}
                onChange={e => setMostrarFechadas(e.target.checked)}
              />
              Mostrar safras fechadas ({totalFechadas})
            </label>
          )}

          {safraSelecionada && (
            <div className="barra-safra__acoes">
              {fechada ? (
                <>
                  <span className="selo selo--alerta">Fechada — somente leitura</span>
                  <button className="btn-secondary btn-sm" onClick={reabrir}>Reabrir safra</button>
                </>
              ) : (
                <>
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() => setDialogo({ tipo: 'renomear', dados: { id: safraSelecionada.id, descricao: safraSelecionada.descricao } })}
                  >Renomear</button>
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() => setDialogo({ tipo: 'fechar' })}
                  >Fechar safra</button>
                  <button
                    className="btn-ghost btn-ghost--perigo btn-sm"
                    onClick={() => setDialogo({ tipo: 'excluir' })}
                  >Excluir</button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Áreas plantadas ── */}
        {!safraId && (
          <EmptyState
            compacto
            icone="safra"
            titulo={safras.length === 0 ? 'Nenhuma safra cadastrada' : 'Selecione uma safra'}
            descricao={safras.length === 0
              ? 'Crie a primeira safra para começar a registrar plantio, custos e produção.'
              : 'Escolha uma safra acima para ver as áreas plantadas.'}
            acao={safras.length === 0 ? { onClick: () => setNovaSafraAberta(true), label: 'Criar safra' } : undefined}
          />
        )}

        {safraId && (carregandoLavouras ? <SkeletonLista itens={3} /> : (
          <>
            {safraLavouras.length > 0 ? (
              <>
                <div className="secao-titulo">
                  <h2>Áreas plantadas — safra {safraSelecionada?.descricao}</h2>
                  <span className="contador">{safraLavouras.length}</span>
                </div>
                <div className="lista-simples">
                  {safraLavouras.map(sl => (
                    <div key={sl.id} className="linha-lista">
                      <div className="item-conteudo">
                        <div className="item-titulo">{sl.lavoura?.descricao}</div>
                        <div className="item-meta">
                          {sl.tipoGrao}
                          {sl.variedade ? ` · ${sl.variedade}` : ''}
                          {sl.areaHectares ? ` · ${sl.areaHectares} ${unidadeArea(sl.lavoura?.tipoArea)}` : ''}
                        </div>
                      </div>
                      <div className="item-acoes">
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => navigate(`/custos?safra=${safraId}&talhao=${sl.id}`)}
                        >Custos</button>
                        {!fechada && (
                          <>
                            <button
                              className="btn-ghost btn-sm"
                              onClick={() => setDialogo({ tipo: 'editarPlantio', dados: { ...sl, areaHectares: sl.areaHectares ?? '' } })}
                            >Editar</button>
                            <button
                              className="btn-ghost btn-ghost--perigo btn-sm"
                              onClick={() => setDialogo({ tipo: 'removerPlantio', dados: sl })}
                            >Remover</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EmptyState
                compacto
                icone="semente"
                titulo="Nenhuma área plantada nesta safra"
                descricao={fechada
                  ? 'A safra está fechada, então não é possível plantar.'
                  : 'Vincule uma lavoura ou estufa para começar a lançar custos e produção.'}
                acao={fechada ? undefined : { onClick: () => setDialogo({ tipo: 'plantar' }), label: 'Plantar nesta safra' }}
              />
            )}

            {!fechada && safraLavouras.length > 0 && (
              <button
                className="btn-secondary botao-largo"
                onClick={() => setDialogo({ tipo: 'plantar' })}
              >
                <Icon nome="adicionar" tamanho={16} />
                Plantar nesta safra / ciclo
              </button>
            )}
          </>
        ))}
      </div>
    </div>
  );
}
